import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "../waterfall-eac/filterPopoverUtils";
import {
  CELL_FG,
  NEG_FG,
  EVEN_BG,
  ODD_BG,
  Z_HEAD,
  GH,
  RH,
  thCoreSC,
  tdBaseSC,
  tdFixedSC,
  tdTotalSC,
  tdTotalScrollSC,
  CARD_STYLE_SC,
  CARD_HEAD_STYLE_SC,
  CARD_TITLE_STYLE_SC,
  CARD_META_STYLE_SC,
  SC_ACCENT,
  CT_OP_ENTRIES,
  ctApplyNumFilter,
  fpBase,
  fpLbl,
  fpTxtI,
  fpNumI,
  fpOpBt,
  fpClr,
  opDropS,
  opItm,
  ctFBtnSt,
  FilterIcon,
  ColumnPicker,
} from "../scenario-yoy/yoyTableShared";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";

const TH_BDR_SC = "#4a4a4c";

/* ── Columns ── */
const FIXED_COLS = [{ key: "month", label: "Month", w: 130, align: "left" }];
let _left = 0;
FIXED_COLS.forEach((c) => {
  c.left = _left;
  _left += c.w;
});
const FIXED_W = _left;

const METRIC_COLS = [
  { key: "days", label: "Working Days", type: "int" },
  { key: "type", label: "Type", type: "text", w: 100 },
  { key: "revenue", label: "Revenue", type: "money", w: 190 },
  { key: "directLabor", label: "Direct Labor", type: "money" },
  { key: "subcontract", label: "Subcontractor", type: "money" },
  { key: "odcAmt", label: "ODCs", type: "money" },
  { key: "tdc", label: "Total Direct Cost", type: "money", w: 190 },
  { key: "grossMargin", label: "Gross Margin", type: "money" },
  { key: "gmPct", label: "Gross Margin %", type: "pct" },
  { key: "indirectCost", label: "Indirect Cost", type: "money" },
  { key: "totalCost", label: "Total Cost", type: "money" },
  { key: "netProfit", label: "Net Profit / Fee", type: "money" },
  { key: "nmPct", label: "Net Margin %", type: "pct" },
];

const COL_W = 150;
const metricW = (m) => m.w ?? COL_W;

/* ── header helpers (single header row) ── */
const FLAT_SH = `inset -1px 0 0 ${TH_BDR_SC}, inset 0 -1px 0 ${TH_BDR_SC}`;
const thFixedCol = (col, isLast) => ({
  ...thCoreSC(0, GH, Z_HEAD + 1, "9px 12px", 1),
  left: col.left,
  width: col.w,
  minWidth: col.w,
  maxWidth: col.w,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: col.align,
  boxShadow: isLast
    ? `4px 0 8px -2px rgba(0,0,0,0.12), ${FLAT_SH}`
    : FLAT_SH,
});
const thMetricCol = () => ({
  ...thCoreSC(0, GH, Z_HEAD, "9px 10px", 1),
  boxShadow: FLAT_SH,
  fontSize: 10,
  fontWeight: 700,
  textAlign: "right",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
});

/* ── formatting ── */
const fmtMoney = (v) => {
  if (v == null || v === "") return "";
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "$0";
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `($${abs})` : `$${abs}`;
};
const fmtInt = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return Math.round(n).toLocaleString("en-US");
};
const fmtPct = (v) => {
  if (v == null || v === "") return "";
  const n = Math.round(Number(v) * 10) / 10;
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0.0%";
  if (n < 0) return `(${Math.abs(n).toFixed(1)}%)`;
  return `${n.toFixed(1)}%`;
};

const fmtCell = (v, type) => {
  if (type === "text") return v ?? "";
  if (type === "pct") return fmtPct(v);
  if (type === "int") return fmtInt(v);
  return fmtMoney(v);
};

const isNeg = (v, type) => {
  if (v == null || v === "") return false;
  const decimals = type === "pct" ? 1 : 0;
  const factor = 10 ** decimals;
  const rounded = Math.round(Number(v) * factor) / factor;
  return rounded < 0;
};
const cellColor = (v, type) => (isNeg(v, type) ? NEG_FG : CELL_FG);

/* ── total from filtered rows ── */
function buildTotals(rows) {
  if (rows.length === 0) return null;
  const s = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const rev = s("revenue");
  const gm = s("grossMargin");
  const np = s("netProfit");
  return {
    month: "Total",
    type: "Total",
    days: "",
    revenue: rev,
    directLabor: s("directLabor"),
    subcontract: s("subcontract"),
    odcAmt: s("odcAmt"),
    tdc: s("tdc"),
    grossMargin: gm,
    gmPct: rev ? (gm / rev) * 100 : 0,
    indirectCost: s("indirectCost"),
    totalCost: s("totalCost"),
    netProfit: np,
    nmPct: rev ? (np / rev) * 100 : 0,
  };
}

export default function ForecastSummaryTable({ rows = [], totalRow: serverTotalRow, version }) {
  const allRows = rows;

  const [visibleMetrics, setVisibleMetrics] = useState(
    METRIC_COLS.map((m) => m.key),
  );
  const activeMetrics = METRIC_COLS.filter((m) =>
    visibleMetrics.includes(m.key),
  );
  const toggleMetric = (key) =>
    setVisibleMetrics((prev) =>
      prev.includes(key)
        ? prev.length > 1
          ? prev.filter((k) => k !== key)
          : prev
        : [...prev, key].sort(
            (a, b) =>
              METRIC_COLS.findIndex((m) => m.key === a) -
              METRIC_COLS.findIndex((m) => m.key === b),
          ),
    );

  const [sortKeys, setSortKeys] = useState([]);
  const [filterText, setFilterText] = useState({ month: "", type: "" });
  const [colFilters, setColFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opPos, setOpPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      ) {
        setOpenFilterKey(null);
        setOpenOpKey(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);
  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const getSortValue = useCallback((row, colKey) => row[colKey], []);

  const getSortColumnLabel = useCallback((colKey) => {
    const fixed = FIXED_COLS.find((c) => c.key === colKey);
    if (fixed) return fixed.label;
    const m = METRIC_COLS.find((mm) => mm.key === colKey);
    return m ? m.label : colKey;
  }, []);

  const hasFilterValue = (key) => {
    if (key in filterText) return !!filterText[key];
    return !!colFilters[key]?.value;
  };

  const toggleFilter = (key, btnEl) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    setFilterPos(calcFilterPos(btnEl, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (!btn) return;
    setOpPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const renderFilterContent = (key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    if (fixed) {
      return (
        <TextFilterContent
          label={fixed.label}
          value={filterText[key]}
          onChange={(v) => setFilterText((p) => ({ ...p, [key]: v }))}
          onClear={() => setFilterText((p) => ({ ...p, [key]: "" }))}
        />
      );
    }
    const metric = METRIC_COLS.find((m) => m.key === key);
    if (metric?.type === "text") {
      const val = filterText[key] || "";
      return (
        <TextFilterContent
          label={metric.label}
          value={val}
          onChange={(v) => setFilterText((p) => ({ ...p, [key]: v }))}
          onClear={() => setFilterText((p) => ({ ...p, [key]: "" }))}
        />
      );
    }
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={metric?.label}
        value={f.value}
        op={f.op}
        onValueChange={(v) => setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))}
        onClear={() => setColFilters((p) => { const n = { ...p }; delete n[key]; return n; })}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      for (const c of FIXED_COLS) {
        const q = filterText[c.key]?.trim().toLowerCase();
        if (q && !String(row[c.key] ?? "").toLowerCase().includes(q))
          return false;
      }
      for (const m of METRIC_COLS) {
        if (m.type !== "text") continue;
        const q = filterText[m.key]?.trim().toLowerCase();
        if (q && !String(row[m.key] ?? "").toLowerCase().includes(q)) return false;
      }
      for (const [key, f] of Object.entries(colFilters)) {
        if (!f?.value) continue;
        const metric = METRIC_COLS.find((m) => m.key === key);
        if (metric?.type === "text") continue;
        if (!ctApplyNumFilter(row[key], f)) return false;
      }
      return true;
    });
  }, [allRows, filterText, colFilters]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const hasTableFilters = useMemo(
    () => Object.values(filterText).some((v) => v && v.trim())
      || Object.values(colFilters).some((f) => f?.value !== "" && f?.value !== undefined),
    [filterText, colFilters],
  );

  const displayTotalRow = useMemo(() => {
    if (serverTotalRow && !hasTableFilters && sortedRows.length === allRows.length) {
      return serverTotalRow;
    }
    return buildTotals(sortedRows) ?? {};
  }, [serverTotalRow, hasTableFilters, sortedRows, allRows.length]);

  const TABLE_W =
    FIXED_W + activeMetrics.reduce((sum, m) => sum + metricW(m), 0);
  const tableStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 12,
    width: TABLE_W,
    minWidth: TABLE_W,
  };

  return (
    <div style={CARD_STYLE_SC}>
      <div style={CARD_HEAD_STYLE_SC}>
        <div>
          <h2 style={CARD_TITLE_STYLE_SC}>Forecast Monthly Summary</h2>
          <div style={CARD_META_STYLE_SC}>
            Revenue · Direct Labor · Costs · Margin · {version}
            {sortKeys.length > 0 && (
              <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                {"  ·  Sorted by "}
                {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
              </span>
            )}
          </div>
        </div>
        <ColumnPicker
          title="Visible Columns"
          items={METRIC_COLS}
          visible={visibleMetrics}
          onToggle={toggleMetric}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <colgroup>
            {FIXED_COLS.map((c) => (
              <col key={c.key} style={{ width: c.w }} />
            ))}
            {activeMetrics.map((m) => (
              <col key={m.key} style={{ width: metricW(m) }} />
            ))}
          </colgroup>

          <thead>
            <tr style={{ height: GH }}>
              {FIXED_COLS.map((c, ci) => {
                const active = openFilterKey === c.key || hasFilterValue(c.key);
                return (
                  <th
                    key={c.key}
                    style={{
                      ...thFixedCol(c, ci === FIXED_COLS.length - 1),
                      cursor: "pointer",
                    }}
                    onClick={onThClick(c.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 4,
                        minWidth: 0,
                      }}
                    >
                      <WaterfallThLabel
                        label={c.label}
                        colKey={c.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        align={c.align}
                      />
                      <button
                        ref={(el) => {
                          filterBtnRefs.current[c.key] = el;
                        }}
                        data-filter-btn
                        style={ctFBtnSt(active)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilter(c.key, e.currentTarget);
                        }}
                      >
                        <FilterIcon />
                      </button>
                    </div>
                  </th>
                );
              })}
              {activeMetrics.map((m) => {
                const active = openFilterKey === m.key || hasFilterValue(m.key);
                const isText = m.type === "text";
                return (
                  <th
                    key={m.key}
                    style={{ ...thMetricCol(), cursor: "pointer" }}
                    onClick={isText ? undefined : onThClick(m.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isText ? "flex-start" : "flex-end",
                        gap: 3,
                        minWidth: 0,
                      }}
                    >
                      <WaterfallThLabel
                        label={m.label}
                        colKey={m.key}
                        sortKeys={sortKeys}
                        getLabel={getSortColumnLabel}
                        align={isText ? "left" : "right"}
                      />
                      <button
                        ref={(el) => {
                          filterBtnRefs.current[m.key] = el;
                        }}
                        data-filter-btn
                        style={ctFBtnSt(active)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilter(m.key, e.currentTarget);
                        }}
                      >
                        <FilterIcon />
                      </button>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {/* Grand total row */}
            <tr style={{ height: RH }}>
              {FIXED_COLS.map((c, ci) => (
                <td
                  key={c.key}
                  style={tdTotalSC(c, ci === FIXED_COLS.length - 1)}
                >
                  {ci === 0 ? "Total" : ""}
                </td>
              ))}
              {activeMetrics.map((m) => {
                const v = displayTotalRow[m.key];
                return (
                  <td
                    key={`tot_${m.key}`}
                    style={{
                      ...tdTotalScrollSC(),
                      color: cellColor(v, m.type),
                      fontStyle: m.type === "pct" ? "italic" : "normal",
                    }}
                  >
                    {fmtCell(v, m.type)}
                  </td>
                );
              })}
            </tr>

            {/* Data rows */}
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={FIXED_COLS.length + activeMetrics.length}
                  style={{
                    padding: "32px",
                    textAlign: "center",
                    color: "#78909C",
                    fontSize: 14,
                  }}
                >
                  No data matches the current filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row, ri) => {
                const bg = ri % 2 === 0 ? EVEN_BG : ODD_BG;
                return (
                  <tr key={row.monthKey ?? row.month + ri} style={{ height: RH }}>
                    {FIXED_COLS.map((c, ci) => (
                      <td
                        key={c.key}
                        style={tdFixedSC(
                          bg,
                          c,
                          ci === 0 ? 600 : 400,
                          ci === FIXED_COLS.length - 1,
                        )}
                        title={String(row[c.key] ?? "")}
                      >
                        {row[c.key] ?? ""}
                      </td>
                    ))}
                    {activeMetrics.map((m) => {
                      const v = row[m.key];
                      return (
                        <td
                          key={m.key}
                          style={{
                            ...tdBaseSC(bg),
                            color: m.type === "text" ? CELL_FG : cellColor(v, m.type),
                            fontStyle: m.type === "pct" ? "italic" : "normal",
                            textAlign: m.type === "text" ? "left" : "right",
                          }}
                        >
                          {fmtCell(v, m.type)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Filter panel portal */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* Operator dropdown portal */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || { op: "gt" }).op}
            onSelect={(op) => {
              setColFilters((p) => ({
                ...p,
                [openOpKey]: { ...(p[openOpKey] || { value: "" }), op },
              }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
